import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import Logger from '../utils/logger.js';

/**
 * Application Insights Service for telemetry and monitoring
 * Provides client-side analytics, error tracking, and performance monitoring
 */
class AppInsightsService {
  constructor() {
    this.appInsights = null;
    this.reactPlugin = null;
    this.isInitialized = false;
    this.instrumentationKey = import.meta.env.VITE_APPINSIGHTS_KEY;
  }

  /**
   * Initialize Application Insights
   */
  initialize() {
    // Skip initialization if no instrumentation key or already initialized
    if (!this.instrumentationKey || this.isInitialized) {
      if (!this.instrumentationKey) {
        Logger.debug('Application Insights: No instrumentation key provided, skipping initialization');
      }
      return;
    }

    try {
      // Create React plugin for component tracking
      this.reactPlugin = new ReactPlugin();

      // Initialize Application Insights
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: this.instrumentationKey,
          extensions: [this.reactPlugin],
          extensionConfig: {
            [this.reactPlugin.identifier]: {
              history: null // Will be set by withRouter if needed
            }
          },
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          autoTrackPageVisitTime: true,
          enableUnhandledPromiseRejectionTracking: true,
          disableExceptionTracking: false,
          disableTelemetry: process.env.NODE_ENV === 'test'
        }
      });

      this.appInsights.loadAppInsights();
      this.isInitialized = true;

      // Set initial context
      // Version is injected at build time from VERSION file via vite.config.js
      const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0-dev';
      this.setContext({
        environment: process.env.NODE_ENV || 'development',
        version: appVersion
      });

      Logger.info('Application Insights initialized successfully');
    } catch (error) {
      Logger.error('Application Insights initialization failed:', error);
    }
  }

  /**
   * Get the React plugin for component integration
   */
  getReactPlugin() {
    return this.reactPlugin;
  }

  /**
   * Track a custom event
   */
  trackEvent(name, properties = {}, measurements = {}) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.trackEvent({
        name,
        properties: {
          ...properties,
          timestamp: new Date().toISOString()
        },
        measurements
      });
    } catch (error) {
      Logger.warn('Failed to track event:', error);
    }
  }

  /**
   * Track a page view
   */
  trackPageView(name, uri) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.trackPageView({
        name,
        uri,
        properties: {
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      Logger.warn('Failed to track page view:', error);
    }
  }

  /**
   * Track an exception
   */
  trackException(error, properties = {}) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.trackException({
        exception: error,
        properties: {
          ...properties,
          timestamp: new Date().toISOString()
        }
      });
    } catch (err) {
      Logger.warn('Failed to track exception:', err);
    }
  }

  /**
   * Track a custom metric
   */
  trackMetric(name, value, properties = {}) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.trackMetric({
        name,
        average: value,
        properties: {
          ...properties,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      Logger.warn('Failed to track metric:', error);
    }
  }

  /**
   * Track dependency (API calls, etc.)
   */
  trackDependency(name, data, duration, success, resultCode) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.trackDependencyData({
        name,
        data,
        duration,
        success,
        resultCode,
        properties: {
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      Logger.warn('Failed to track dependency:', error);
    }
  }

  /**
   * Set user context
   */
  setUser(userId, accountId = null) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.setAuthenticatedUserContext(userId, accountId);
    } catch (error) {
      Logger.warn('Failed to set user context:', error);
    }
  }

  /**
   * Clear user context
   */
  clearUser() {
    if (!this.isInitialized) return;

    try {
      this.appInsights.clearAuthenticatedUserContext();
    } catch (error) {
      Logger.warn('Failed to clear user context:', error);
    }
  }

  /**
   * Set custom properties for all telemetry
   */
  setContext(properties) {
    if (!this.isInitialized) return;

    try {
      this.appInsights.addTelemetryInitializer(envelope => {
        if (envelope.data && envelope.data.baseData) {
          envelope.data.baseData.properties = {
            ...envelope.data.baseData.properties,
            ...properties
          };
        }
      });
    } catch (error) {
      Logger.warn('Failed to set context:', error);
    }
  }

  /**
   * Flush all pending telemetry
   */
  flush() {
    if (!this.isInitialized) return;

    try {
      this.appInsights.flush();
    } catch (error) {
      Logger.warn('Failed to flush telemetry:', error);
    }
  }

  /**
   * Start timing an operation
   */
  startTimer(name) {
    const startTime = Date.now();
    return {
      name,
      startTime,
      end: () => {
        const duration = Date.now() - startTime;
        this.trackMetric(`${name}_duration`, duration, {
          operationType: 'timer'
        });
        return duration;
      }
    };
  }

  /**
   * Track business metrics specific to fleet management
   */
  trackFleetMetric(metricType, value, properties = {}) {
    this.trackMetric(`fleet_${metricType}`, value, {
      ...properties,
      category: 'fleet_management'
    });
  }

  /**
   * Track user interactions with the scheduler
   */
  trackSchedulerEvent(action, properties = {}) {
    this.trackEvent('scheduler_interaction', {
      ...properties,
      action,
      component: 'scheduler'
    });
  }

  /**
   * Track authentication events
   */
  trackAuthEvent(eventType, properties = {}) {
    this.trackEvent('authentication', {
      ...properties,
      eventType,
      component: 'auth'
    });
  }

  /**
   * Track Web Vitals metrics (Issue #190 - Performance Optimization)
   * Maps web-vitals format to Application Insights
   *
   * @param {Object} metric - Web Vitals metric object
   * @param {string} metric.name - Metric name (CLS, FID, FCP, LCP, TTFB, INP)
   * @param {number} metric.value - Metric value
   * @param {string} metric.id - Unique metric instance ID
   * @param {string} metric.rating - 'good' | 'needs-improvement' | 'poor'
   * @param {number} metric.delta - Change since last report
   * @param {Object} metric.entries - Performance entries that contributed
   */
  trackWebVital(metric) {
    if (!this.isInitialized) return;

    try {
      const { name, value, id, rating, delta, navigationType } = metric;

      // Track as a custom metric with the web vital name
      this.appInsights.trackMetric({
        name: `WebVital.${name}`,
        average: value,
        properties: {
          metricId: id,
          rating: rating || this._getWebVitalRating(name, value),
          delta,
          navigationType: navigationType || 'unknown',
          url: window.location.pathname,
          timestamp: new Date().toISOString()
        }
      });

      // Track ratings as events for easier filtering
      this.trackEvent(
        'WebVitalMeasurement',
        {
          metricName: name,
          rating: rating || this._getWebVitalRating(name, value),
          navigationType: navigationType || 'unknown',
          url: window.location.pathname
        },
        {
          value,
          delta
        }
      );

      Logger.debug(`Web Vital tracked: ${name}=${value} (${rating || 'unknown'})`);
    } catch (error) {
      Logger.warn('Failed to track Web Vital:', error);
    }
  }

  /**
   * Get rating for a web vital value based on thresholds
   * @private
   */
  _getWebVitalRating(name, value) {
    const thresholds = {
      CLS: { good: 0.1, poor: 0.25 },
      FID: { good: 100, poor: 300 },
      FCP: { good: 1800, poor: 3000 },
      LCP: { good: 2500, poor: 4000 },
      TTFB: { good: 800, poor: 1800 },
      INP: { good: 200, poor: 500 }
    };

    const threshold = thresholds[name];
    if (!threshold) return 'unknown';

    if (value <= threshold.good) return 'good';
    if (value > threshold.poor) return 'poor';
    return 'needs-improvement';
  }

  /**
   * Create a callback function for reportWebVitals
   * @returns {Function} Callback for web-vitals library
   */
  createWebVitalsReporter() {
    return metric => this.trackWebVital(metric);
  }
}

// Create singleton instance
const appInsightsService = new AppInsightsService();

export default appInsightsService;
